import React from 'react';

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import cn from 'classnames';
import { useIntl } from 'react-intl';

import IconFemtasy from 'components/IconFemtasy';
import { FemtasyIcon } from 'components/IconFemtasy/iconsType';
import Message from 'components/Message';
import Typography, { TypographyVariant } from 'components/Typography';

import { IntlMsgShape, isIntlMsgShape } from 'helpers/consts';

type anchorVariant = 'top' | 'bottom';

export interface IProps {
  anchor: anchorVariant;
  ariaLabel?: string | IntlMsgShape;
  children: React.ReactNode;
  header?: string | IntlMsgShape;
  headerVariant?: TypographyVariant;
  icon: FemtasyIcon;
  onClose: () => void;
  open: boolean;
}

const anchorClasses: Record<anchorVariant, string> = {
  top: 'top-0 rounded-b-2xl animate-slide-in-top',
  bottom: 'bottom-0 rounded-t-2xl animate-slide-in-bottom',
};

const Drawer = ({
  anchor,
  ariaLabel,
  children,
  header,
  headerVariant = 'h2',
  icon,
  onClose,
  open,
}: IProps) => {
  const intl = useIntl();
  const dialogLabel =
    !header && ariaLabel
      ? isIntlMsgShape(ariaLabel)
        ? intl.formatMessage(ariaLabel)
        : ariaLabel
      : undefined;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label={dialogLabel}
      className="fixed inset-0 z-drawer"
    >
      <div className="absolute inset-0 bg-dark-body/85 animate-fade-in" />
      <DialogPanel
        className={cn(
          'absolute inset-x-0 mx-auto flex max-h-full w-full max-w-screen-md flex-col gap-8 overflow-y-auto bg-soft-sensual-audios p-4',
          anchorClasses[anchor],
        )}
      >
        <div className="flex w-full items-center">
          {header ? (
            <DialogTitle as="div">
              <Typography variant={headerVariant}>
                <Message message={header} />
              </Typography>
            </DialogTitle>
          ) : null}
          <IconFemtasy
            className="ml-auto"
            icon={icon}
            onClick={() => onClose()}
            size="24"
          />
        </div>
        {children}
      </DialogPanel>
    </Dialog>
  );
};

export default Drawer;
